import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Filter,
  TrendingDown,
  Clock,
  DollarSign,
  FileText,
  GraduationCap,
  Zap,
  AlertCircle
} from 'lucide-react';
import { api } from '../utils/api';

interface PreFilterMetrics {
  totalPosts: number;
  filteredCount: number;
  filterRate: number;
  breakdown: {
    news: number;
    educational: number;
    historical: number;
    lowRelevance: number;
  };
  performance: {
    avgTimeMs: number;
    p95TimeMs: number;
    successRate: number;
  };
  accuracy: {
    estimatedFalsePositivesAvoided: number;
    potentialFalseNegatives: number;
    costSavings: number;
  };
}

interface DailyStats {
  date: string;
  total: number;
  filtered: number;
  news: number;
  educational: number;
  operational: number;
  avg_time_ms: number;
  avg_confidence: number;
}

export function PreFilterStats() {
  const [metrics, setMetrics] = useState<PreFilterMetrics | null>(null);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d'>('24h');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMetrics();
    fetchDailyStats();
    const interval = setInterval(fetchMetrics, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [timeRange]);

  const fetchMetrics = async () => {
    try {
      const endpoint = timeRange === '1h' ? 'last_hour' : 
                      timeRange === '24h' ? 'last_24h' : 'last_7d';
      const response = await api.get(`/api/prefilter/stats`);
      if (response.data[endpoint]) {
        setMetrics(response.data[endpoint]);
      }
      setError(null);
    } catch (err) {
      setError('Failed to fetch pre-filter metrics');
      console.error('Error fetching metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDailyStats = async () => {
    try {
      const days = timeRange === '1h' ? 1 : timeRange === '24h' ? 1 : 7;
      const response = await api.get(`/api/prefilter/daily?days=${days}`);
      setDailyStats(response.data || []);
    } catch (err) {
      console.error('Error fetching daily stats:', err);
    }
  };

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case 'news': return <FileText className="h-4 w-4" />;
      case 'educational': return <GraduationCap className="h-4 w-4" />;
      case 'operational': return <Zap className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-red-500">
            <AlertCircle className="h-8 w-8 mx-auto mb-2" />
            <p>{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!metrics) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Time Range Selector */}
      <div className="flex justify-end space-x-2">
        {(['1h', '24h', '7d'] as const).map((range) => (
          <button
            key={range}
            onClick={() => setTimeRange(range)}
            className={`px-3 py-1 rounded-md text-sm ${
              timeRange === range
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {range === '1h' ? 'Last Hour' : range === '24h' ? 'Last 24h' : 'Last 7 Days'}
          </button>
        ))}
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Filter className="h-4 w-4 mr-2" />
              Filter Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(metrics.filterRate * 100).toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {metrics.filteredCount} of {metrics.totalPosts} posts
            </p>
            <Progress value={metrics.filterRate * 100} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <TrendingDown className="h-4 w-4 mr-2" />
              False Positives Avoided
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metrics.accuracy.estimatedFalsePositivesAvoided}
            </div>
            <p className="text-xs text-muted-foreground">
              Posts correctly filtered
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              Avg Processing Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {metrics.performance.avgTimeMs.toFixed(0)}ms
            </div>
            <p className="text-xs text-muted-foreground">
              P95: {metrics.performance.p95TimeMs}ms
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <DollarSign className="h-4 w-4 mr-2" />
              Cost Savings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(metrics.accuracy.costSavings)}
            </div>
            <p className="text-xs text-muted-foreground">
              Saved on AI processing
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Content Type Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Content Type Breakdown</CardTitle>
          <CardDescription>
            Distribution of filtered content by type
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Object.entries({
              news: { label: 'News Articles', icon: <FileText className="h-4 w-4" /> },
              educational: { label: 'Educational Content', icon: <GraduationCap className="h-4 w-4" /> },
              historical: { label: 'Historical Reports', icon: <Clock className="h-4 w-4" /> },
              lowRelevance: { label: 'Low Relevance', icon: <TrendingDown className="h-4 w-4" /> }
            }).map(([key, config]) => {
              const count = metrics.breakdown[key as keyof typeof metrics.breakdown];
              const percentage = metrics.filteredCount > 0 
                ? (count / metrics.filteredCount * 100).toFixed(1)
                : '0';
              
              return (
                <div key={key} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {config.icon}
                    <span className="text-sm font-medium">{config.label}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="secondary">{count}</Badge>
                    <span className="text-sm text-muted-foreground w-12 text-right">
                      {percentage}%
                    </span>
                    <div className="w-24">
                      <Progress value={parseFloat(percentage)} className="h-2" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Performance Metrics</CardTitle>
          <CardDescription>
            Pre-filter processing performance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
              <p className="text-2xl font-bold">
                {(metrics.performance.successRate * 100).toFixed(1)}%
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Avg Time</p>
              <p className="text-2xl font-bold">
                {metrics.performance.avgTimeMs.toFixed(1)}ms
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">P95 Time</p>
              <p className="text-2xl font-bold">
                {metrics.performance.p95TimeMs}ms
              </p>
            </div>
          </div>
          
          {/* Performance indicator */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm">Processing Speed</span>
              <span className="text-sm text-muted-foreground">
                Target: &lt;250ms
              </span>
            </div>
            <Progress 
              value={Math.min((250 - metrics.performance.avgTimeMs) / 250 * 100, 100)} 
              className={`h-2 ${
                metrics.performance.avgTimeMs < 100 ? 'bg-green-100' :
                metrics.performance.avgTimeMs < 200 ? 'bg-yellow-100' :
                'bg-red-100'
              }`}
            />
          </div>
        </CardContent>
      </Card>

      {/* Daily Trend (if we have data) */}
      {dailyStats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Daily Activity</CardTitle>
            <CardDescription>
              Pre-filter activity over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {dailyStats.map((day) => (
                <div key={day.date} className="flex items-center justify-between py-2 border-b last:border-0">
                  <span className="text-sm font-medium">
                    {new Date(day.date).toLocaleDateString()}
                  </span>
                  <div className="flex items-center space-x-4">
                    <Badge variant="outline">
                      {day.filtered}/{day.total} filtered
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {day.avg_time_ms.toFixed(0)}ms avg
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Potential Issues Alert */}
      {metrics.accuracy.potentialFalseNegatives > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-yellow-600" />
              Potential False Negatives
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              {metrics.accuracy.potentialFalseNegatives} posts may have been incorrectly filtered.
              Consider reviewing posts with low confidence scores.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}